import React, { useState, useEffect, useRef } from "react";

// options: array of { value: string; label: string }
const PicklistValueDropdown = ({ value, onChange, options, disabledOptions = new Set() }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize selected values from value prop (comma-separated string)
  useEffect(() => {
    if (value && value.trim() !== "") {
      setSelectedValues(value.split(",").map(v => v.trim()));
    } else {
      setSelectedValues([]);
    }
  }, [value]);

  // Close dropdown when clicking outside and save
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (isOpen) {
          // Save the selected values when closing
          const valueString = selectedValues.join(",");
          onChange(valueString);
          setIsOpen(false);
          setSearchTerm("");
        }
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, selectedValues, onChange]);

  const handleDropdownClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } else {
      // When closing, save the values
      const valueString = selectedValues.join(",");
      onChange(valueString);
    }
  };

  const handleOptionSelect = (optionValue) => {
    if (!optionValue) {
      // Clear all selections
      setSelectedValues([]);
      return;
    }

    // Don't allow selecting disabled options
    if (disabledOptions.has(optionValue)) {
      return;
    }

    // Toggle selection
    setSelectedValues(prev => {
      if (prev.includes(optionValue)) {
        // Remove if already selected
        return prev.filter(v => v !== optionValue);
      } else {
        // Add to selections
        return [...prev, optionValue];
      }
    });
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return "-- None --";

    const findLabel = (val) => {
      const option = options.find((opt) => opt.value === val);
      return option ? option.label : val;
    };

    if (selectedValues.length === 1) {
      return findLabel(selectedValues[0]);
    }

    return `${findLabel(selectedValues[0])} (${selectedValues.length} values selected)`;
  };

  const getOrderNumber = (optionValue) => {
    return selectedValues.indexOf(optionValue) + 1;
  };

  // Filter options based on search term (by label)
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="picklist-custom-dropdown" ref={dropdownRef}>
      <div
        className={`picklist-dropdown-header ${isOpen ? 'open' : ''}`}
        onClick={handleDropdownClick}
      >
        <span className="picklist-dropdown-text">{getDisplayText()}</span>
        <svg
          className={`picklist-dropdown-arrow ${isOpen ? "open" : ""}`}
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1L5 5L9 1"
            stroke="#706E6B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="picklist-dropdown-menu">
          <div className="picklist-dropdown-options">
            {/* None option */}
            <div
              className={`picklist-dropdown-option ${selectedValues.length === 0 ? "selected" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                handleOptionSelect("");
              }}
            >
              <span className="picklist-option-label">-- None --</span>
              {selectedValues.length === 0 && (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M13.3337 4L6.00033 11.3333L2.66699 8"
                    stroke="#40A0C1"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>

            {/* Filtered options */}
            {filteredOptions.map((option, index) => {
              const optionValue = option.value;
              const isSelected = selectedValues.includes(optionValue);
              const orderNumber = getOrderNumber(optionValue);
              const isDisabled = disabledOptions.has(optionValue);

              return (
                <div
                  key={index}
                  className={`picklist-dropdown-option ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDisabled) {
                      handleOptionSelect(optionValue);
                    }
                  }}
                  style={{
                    textDecoration: isDisabled ? 'line-through' : 'none',
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span className="picklist-option-label">
                    {option.label}
                    {isSelected && (
                      <span className="picklist-option-badges">
                        <span className="picklist-badge">
                          ({orderNumber})
                        </span>
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13.3337 4L6.00033 11.3333L2.66699 8"
                        stroke="#40A0C1"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              );
            })}

            {filteredOptions.length === 0 && searchTerm && (
              <div className="picklist-dropdown-option disabled">
                <span className="picklist-option-label">No options found</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PicklistValueDropdown;
